import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  Facebook,
  Instagram,
  type LucideIcon,
  MessageCircle,
  MessageSquare,
  Phone,
  Rows3,
  SlidersHorizontal,
} from 'lucide-react';

const PLATFORM_OPTIONS: { value: string; label: string; icon: LucideIcon }[] = [
  { value: 'all', label: 'All platforms', icon: Rows3 },
  { value: 'instagram_dm', label: 'Instagram', icon: Instagram },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'facebook_messenger', label: 'Messenger', icon: Facebook },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'phone', label: 'Phone calls', icon: Phone },
];

/**
 * The filter control to the right of the inbox search. Holds the platform
 * choice (it used to be a dropdown in the page header); a dot on the button
 * says a filter is narrowing the list, so an emptier-than-expected inbox has
 * an explanation in sight.
 */
export function InboxFilterButton({
  platform,
  onPlatformChange,
  className,
}: {
  platform: string;
  onPlatformChange: (value: string) => void;
  className?: string;
}) {
  const isFiltered = platform !== 'all';
  const current = PLATFORM_OPTIONS.find((o) => o.value === platform);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={
            isFiltered && current
              ? `Filter conversations: ${current.label}`
              : 'Filter conversations'
          }
          className={cn('relative size-10 shrink-0 rounded-[10px]', className)}
          size="icon"
          type="button"
          variant="outline"
        >
          <SlidersHorizontal className="size-[18px]" aria-hidden />
          {isFiltered ? (
            <span
              aria-hidden
              className="absolute top-1.5 right-1.5 size-2 rounded-full bg-primary"
            />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Platform</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          onValueChange={onPlatformChange}
          value={platform}
        >
          {PLATFORM_OPTIONS.map(({ value, label, icon: Icon }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon className="size-4 text-muted-foreground" aria-hidden />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
